import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import httpClient from "../utils/httpClients";
import { usePlayer } from "../../PlayerContext";

function Trending() {
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [timePeriod, setTimePeriod] = useState("daily");
  const [searchQuery, setSearchQuery] = useState("");
  const [likedSongs, setLikedSongs] = useState(new Set());
  const navigate = useNavigate();
  const [playCountTracked, setPlayCountTracked] = useState(false);
  const [lastPlayedCountTrackedSongId, setLastPlayedCountTrackedSongId] =
    useState(null);
  const playTimerRef = useRef(null);
  const [sharePopup, setSharePopup] = useState({ open: false, song: null });

  // Playlist states
  const [playlistModal, setPlaylistModal] = useState({
    open: false,
    song: null,
  });
  const [playlists, setPlaylists] = useState([]);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    description: "",
    coverImage: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [playlistLoading, setPlaylistLoading] = useState(false);

  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    seek,
    duration,
    triggerQueueUpdate,
    tracks,
    playTrackById,
    stopPlayback,
  } = usePlayer();

  const [selectedSongId, setSelectedSongId] = useState(null);
  const [hasInitiatedPlayback, setHasInitiatedPlayback] = useState(false);

  useEffect(() => {
    if (!selectedSongId && filteredSongs.length > 0) {
      setSelectedSongId(filteredSongs[0]._id);
    }
  }, [filteredSongs, selectedSongId]);

  const selectedSong =
    filteredSongs.find((song) => song._id === selectedSongId) || null;

  const displayTrack =
    selectedSong || (filteredSongs.length > 0 ? filteredSongs[0] : null);

  const isDisplayTrackPlaying =
    hasInitiatedPlayback &&
    isPlaying &&
    currentTrack?._id &&
    currentTrack?._id === displayTrack?._id;

  // Fetch playlists
  const fetchPlaylists = async () => {
    try {
      const res = await httpClient.get("/user/playlist/get-playlists");
      if (res.data && res.data.success) {
        setPlaylists(res.data.data.playlist || []);
      }
    } catch (err) {
      console.error("Error fetching playlists:", err);
    }
  };

  // Open playlist modal
  const openPlaylistModal = (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaylistModal({ open: true, song });
    fetchPlaylists();
    setOpenDropdown(null);
  };

  // Close playlist modal
  const closePlaylistModal = () => {
    setPlaylistModal({ open: false, song: null });
    setShowCreateForm(false);
    setNewPlaylist({ name: "", description: "", coverImage: null });
  };

  // Create new playlist
  const createNewPlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      alert("Please enter playlist name");
      return;
    }

    try {
      setPlaylistLoading(true);
      const formData = new FormData();
      formData.append("name", newPlaylist.name);
      formData.append("description", newPlaylist.description);
      if (newPlaylist.coverImage) {
        formData.append("coverImage", newPlaylist.coverImage);
      }

      const res = await httpClient.post(
        "/user/playlist/create-playlist",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data && res.data.success) {
        await fetchPlaylists();
        setShowCreateForm(false);
        setNewPlaylist({ name: "", description: "", coverImage: null });

        if (playlistModal.song) {
          await addToPlaylist(
            res.data.data.playlist._id,
            playlistModal.song._id,
          );
        }
      }
    } catch (err) {
      console.error("Error creating playlist:", err);
      alert("Failed to create playlist");
    } finally {
      setPlaylistLoading(false);
    }
  };

  // Add song to playlist
  const addToPlaylist = async (playlistId, songId) => {
    try {
      const params = new URLSearchParams({
        playlistId: playlistId,
        itemId: songId,
        itemType: "Music",
      });

      const res = await httpClient.post(`/user/playlist/add-item?${params}`);

      if (res.data && res.data.success) {
        closePlaylistModal();
      } else {
      }
    } catch (err) {
      console.error("Error adding song to playlist:", err);
    }
  };

  // Handle file input
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPlaylist((prev) => ({
        ...prev,
        coverImage: file,
      }));
    }
  };

  // Share popup
  const openSharePopup = (song, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharePopup({ open: true, song });
  };

  const closeSharePopup = () => {
    setSharePopup({ open: false, song: null });
  };

  const handleCopy = async () => {
    const text = generateShareableUrl(sharePopup.song._id);
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = 0;
      textArea.style.left = 0;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const isSongCurrentlyPlaying = (songId) => {
    return currentTrack && currentTrack._id === songId;
  };

  const getQueuePosition = (songId) => {
    const index = tracks.findIndex((track) => track._id === songId);
    return index >= 0 ? index + 1 : null;
  };

  // Queue controller (used for "add to queue" only — play actions go through PlayerContext)
  const handleQueueAction = async (action, trackId = null) => {
    try {
      const payload = { action };
      if (trackId) {
        payload.trackId = trackId;
      }

      const res = await httpClient.post("user/queue/queue-controller", payload, {
        skipLoader: true,
        skipErrorToast: true,
      });
      if (res.data && res.data.success) {
        triggerQueueUpdate();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Queue action error:", error);
      return false;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const fetchTrendingSongs = async (period = timePeriod, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const res = await httpClient.get(
        `user/music/trending-music?type=${period}&search=${search}&page=1&limit=20`,
      );

      if (res.data && res.data.success) {
        const songsData = res.data.data?.trendingMusic || [];
        setSongs(songsData);
        setFilteredSongs(songsData);

        const likedSongIds = new Set();
        songsData.forEach((song) => {
          if (song.isLiked) likedSongIds.add(song._id);
        });
        setLikedSongs(likedSongIds);
      } else {
        setError("Failed to fetch trending songs");
      }
    } catch (err) {
      setError("Error fetching trending songs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim() === "") {
      setFilteredSongs(songs);
    } else {
      const filtered = songs.filter(
        (song) =>
          song.title.toLowerCase().includes(query.toLowerCase()) ||
          (song.artist &&
            song.artist.toLowerCase().includes(query.toLowerCase())),
      );
      setFilteredSongs(filtered);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTrendingSongs(timePeriod, searchQuery);
  };

  const handleLikeSong = async (contentId, isCurrentlyLiked) => {
    const newLiked = !isCurrentlyLiked;

    // Optimistic update: reflect immediately
    const prevLikedSongs = new Set(likedSongs);
    const prevSongs = [...songs];
    const prevFiltered = [...filteredSongs];

    const newLikedSongs = new Set(likedSongs);
    if (isCurrentlyLiked) newLikedSongs.delete(contentId);
    else newLikedSongs.add(contentId);
    setLikedSongs(newLikedSongs);

    const updatedSongs = songs.map((song) =>
      song._id === contentId ? { ...song, isLiked: newLiked } : song,
    );
    setSongs(updatedSongs);
    setFilteredSongs(updatedSongs);

    try {
      const action = isCurrentlyLiked ? "dislike" : "like";
      const res = await httpClient.post(
        `/user/play-history/like-content/${contentId}/${action}`,
        {},
        { skipToast: true, skipLoader: true },
      );
      if (!res.data?.success) {
        setLikedSongs(prevLikedSongs);
        setSongs(prevSongs);
        setFilteredSongs(prevFiltered);
      }
    } catch (err) {
      console.error("Error liking/disliking song:", err);
      setLikedSongs(prevLikedSongs);
      setSongs(prevSongs);
      setFilteredSongs(prevFiltered);
    }
  };

  // Play song — no API if same song
  const playSong = async (song) => {
    if (!song?._id) return;

    setSelectedSongId(song._id);
    setHasInitiatedPlayback(true);

    if (currentTrack?._id === song._id) {
      if (!isPlaying) await togglePlayPause();
      return;
    }

    if (typeof stopPlayback === "function") {
      stopPlayback();
    }
    await playTrackById(song._id, "audio");
  };

  // Play count is recorded only by PlayerContext (single source of truth)

  // Play All
  const playAllSongs = async () => {
    if (filteredSongs.length === 0) return;
    const firstSong = filteredSongs[0];

    setSelectedSongId(firstSong._id);
    setHasInitiatedPlayback(true);

    if (currentTrack?._id === firstSong._id) {
      if (!isPlaying) await togglePlayPause();
      return;
    }

    if (typeof stopPlayback === "function") {
      stopPlayback();
    }
    const trackIds = filteredSongs.map((s) => s._id);
    await playTrackById(trackIds, "audio");
  };

  const handleTopPlayPauseClick = async () => {
    if (!displayTrack) return;
    setHasInitiatedPlayback(true);
    if (currentTrack?._id !== displayTrack._id) {
      await playSong(displayTrack);
      return;
    }
    await togglePlayPause();
  };

  // Add to Queue (only this option)
  const addToQueue = async (trackId) => {
    const success = await handleQueueAction("add", trackId);
  };

  const sendToArtist = (artistId) => artistId && navigate(`/art/${artistId}`);
  const sendToAlbum = (albumId, isAlbum) =>
    isAlbum && albumId && navigate(`/album/${albumId}`);

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
    fetchTrendingSongs(period, searchQuery);
  };

  const toggleDropdown = (id) =>
    setOpenDropdown(openDropdown === id ? null : id);

  useEffect(() => {
    const handleOutside = (e) => {
      if (!e.target.closest(".dropdown")) setOpenDropdown(null);
      if (
        !e.target.closest(".share-popup") &&
        !e.target.closest(".single-item__export")
      )
        closeSharePopup();
      if (
        !e.target.closest(".playlist-popup") &&
        !e.target.closest(".dropdown-item")
      )
        closePlaylistModal();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    fetchTrendingSongs();
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (loading)
    return (
      <div className="content-inner pb-0 container-fluid">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="content-inner pb-0 container-fluid">
        <div className="alert alert-danger">{error}</div>
      </div>
    );

  // const generateShareableUrl = (songId) =>
  //   `https://webmobrildemo.com/volumetv/music/${encodeURIComponent(songId)}`;
  // console.log("basic url", process.env.REACT_APP_FRONTEND_URL);
  const generateShareableUrl = (songId) => {
    // Get base URL from environment variable or use current origin
    const baseUrl =
      process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    // Ensure /volumetv path is included
    // const basePath = baseUrl.endsWith("/volumetv")
    //   ? baseUrl
    //   : baseUrl.endsWith("/")
    //     ? `${baseUrl}volumetv`
    //     : `${baseUrl}/volumetv`;
    return `${baseUrl}music/${encodeURIComponent(songId)}`;
  };

  const shareOnSocialMedia = (platform, song) => {
    const url = generateShareableUrl(song._id);
    const text = encodeURIComponent(song.title);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(links[platform], "_blank", "width=600,height=400");
  };
  console.log("ds", currentTrack);
  console.log("dsdd", currentTrack);

  return (
    <div className="content-inner pb-0 container-fluid trending-page" id="page_layout">
      <style>
        {`
          @media (max-width: 575.98px) {
            .trending-page .main__filter {
              display: flex;
              flex-direction: column;
              gap: 12px;
              padding: 12px !important;
              border-radius: 12px;
            }

            .trending-page .main__filter-search {
              width: 100%;
              margin: 0;
            }

            .trending-page .main__filter-wrap {
              width: 100%;
              display: flex;
              justify-content: flex-end;
              order: 3;
              margin: 0;
              padding: 0px;
            }

            .trending-page .main__filter-wrap .release__buy.filter {
              width: auto;
              min-width: 100%;
              height: 40px;
              padding: 0 16px;
              border-radius: 10px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              margin: 0;
              line-height: 1;
            }

            .trending-page .slider-radio {
              width: 100%;
              order: 2;
              margin: 0;
            }

            .trending-page .release__list {
              margin-top: 8rem !important;
            }
          }
        `}
      </style>
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              Trending
            </li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page">
            <h2 className="card-title text-capitalize mt-3">Trending Songs</h2>
          </div>
        </div>

        <div className="">
          <div className="">
            <div className="main__filter p-2">
              <form
                onSubmit={handleSearchSubmit}
                className="main__filter-search"
              >
                <input
                  type="text"
                  placeholder="Search songs or artists..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <button type="submit">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z" />
                  </svg>
                </button>
              </form>

              <div className="main__filter-wrap">
                <a
                  href="#!"
                  className="release__buy open-modal filter"
                  onClick={playAllSongs}
                >
                  <i className="fas fa-play"></i>Play All{" "}
                </a>
              </div>

              <div className="slider-radio">
                <input
                  type="radio"
                  name="grade"
                  id="featured"
                  checked={timePeriod === "daily"}
                  onChange={() => handleTimePeriodChange("daily")}
                />
                <label htmlFor="featured">Daily</label>
                <input
                  type="radio"
                  name="grade"
                  id="popular"
                  checked={timePeriod === "weekly"}
                  onChange={() => handleTimePeriodChange("weekly")}
                />
                <label htmlFor="popular">Weekly</label>
                <input
                  type="radio"
                  name="grade"
                  id="newest"
                  checked={timePeriod === "monthly"}
                  onChange={() => handleTimePeriodChange("monthly")}
                />
                <label htmlFor="newest">Monthly</label>
                <div className="sliding-piece" />
              </div>
            </div>
          </div>
        </div>

        {filteredSongs.length > 0 ? (
          <div className="col-12">
            <div className="release">
              <div className="release__content">
                <div className="release__cover">
                  <img
                    src={
                      displayTrack?.coverImageUrl ||
                      `${process.env.PUBLIC_URL}/img/covers/cover3.jpg`
                    }
                    alt="Trending songs"
                  />
                </div>
                <div className="release__stat">
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M21.65,2.24a1,1,0,0,0-.8-.23l-13,2A1,1,0,0,0,7,5V15.35A3.45,3.45,0,0,0,5.5,15,3.5,3.5,0,1,0,9,18.5V10.86L20,9.17v4.18A3.45,3.45,0,0,0,18.5,13,3.5,3.5,0,1,0,22,16.5V3A1,1,0,0,0,21.65,2.24ZM5.5,20A1.5,1.5,0,1,1,7,18.5,1.5,1.5,0,0,1,5.5,20Zm13-2A1.5,1.5,0,1,1,20,16.5,1.5,1.5,0,0,1,18.5,18ZM20,7.14,9,8.83v-3L20,4.17Z" />
                    </svg>{" "}
                    {filteredSongs.length} tracks
                  </span>
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path d="M20,13.18V11A8,8,0,0,0,4,11v2.18A3,3,0,0,0,2,16v2a3,3,0,0,0,3,3H8a1,1,0,0,0,1-1V14a1,1,0,0,0-1-1H6V11a6,6,0,0,1,12,0v2H16a1,1,0,0,0-1,1v6a1,1,0,0,0,1,1h3a3,3,0,0,0,3-3V16A3,3,0,0,0,20,13.18ZM7,15v4H5a1,1,0,0,1-1-1V16a1,1,0,0,1,1-1Zm13,3a1,1,0,0,1-1,1H17V15h2a1,1,0,0,1,1,1Z"></path>
                    </svg>
                    {displayTrack?.playCount || 0}
                  </span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <button
                    type="button"
                    className="release__buy open-modal"
                    onClick={handleTopPlayPauseClick}
                    disabled={!displayTrack}
                  >
                    <i
                      className={
                        isDisplayTrackPlaying ? "fas fa-pause" : "fas fa-play"
                      }
                    ></i>
                    {isDisplayTrackPlaying ? "Pause" : "Play"}
                  </button>
                </div>
              </div>

              <div className="release__list trending">
                <ul className="main__list main__list--playlist main__list--dashbox ps-0">
                  {filteredSongs.map((song, index) => {
                    const contentId = song._id;
                    const isLiked = likedSongs.has(contentId);
                    const isCurrentlyPlaying =
                      isSongCurrentlyPlaying(contentId);
                    const queuePosition = getQueuePosition(contentId);
                    const isDropdownOpen = openDropdown === contentId;
                    const ArtistId = song.artistIds?.[0];
                    const isAlbhum = song.isAlbum;
                    const AlbhumId = song.parentId;

                    return (
                      <li
                        key={contentId}
                        className={`single-item list-1 ${
                          isCurrentlyPlaying ? "active-episode" : ""
                        }`}
                        style={{
                          backgroundColor: isCurrentlyPlaying
                            ? "rgba(255, 255, 255, 0.1)"
                            : "transparent",
                          borderLeft: isCurrentlyPlaying
                            ? "3px solid #1DB954"
                            : "3px solid transparent",
                        }}
                      >
                        <div
                          className="single-item__cover ms-2"
                          onClick={() => playSong(song)}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            src={
                              song?.coverImageUrl ||
                              `${process.env.PUBLIC_URL}/img/covers/cover.svg`
                            }
                            alt={song.title}
                          />
                          {isCurrentlyPlaying && isPlaying ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              style={{ fill: "#1DB954" }}
                            >
                              <path d="M16,2a3,3,0,0,0-3,3V19a3,3,0,0,0,6,0V5A3,3,0,0,0,16,2Zm1,17a1,1,0,0,1-2,0V5a1,1,0,0,1,2,0ZM8,2A3,3,0,0,0,5,5V19a3,3,0,0,0,6,0V5A3,3,0,0,0,8,2ZM9,19a1,1,0,0,1-2,0V5A1,1,0,0,1,9,5Z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              style={{
                                fill: isCurrentlyPlaying
                                  ? "#1DB954"
                                  : "#FFFFFF",
                              }}
                            >
                              <path d="M18.54,9,8.88,3.46a3.42,3.42,0,0,0-5.13,3V17.58A3.42,3.42,0,0,0,7.17,21a3.43,3.43,0,0,0,1.71-.46L18.54,15a3.42,3.42,0,0,0,0-5.92Zm-1,4.19L7.88,18.81a1.44,1.44,0,0,1-1.42,0,1.42,1.42,0,0,1-.71-1.23V6.42a1.42,1.42,0,0,1,.71-1.23A1.51,1.51,0,0,1,7.17,5a1.54,1.54,0,0,1,.71.19l9.66,5.58a1.42,1.42,0,0,1,0,2.46Z" />
                            </svg>
                          )}
                        </div>

                        <div className="single-item__title">
                          <h4
                            onClick={() =>
                              isAlbhum && sendToAlbum(AlbhumId, isAlbhum)
                            }
                            style={{ cursor: isAlbhum ? "pointer" : "default" }}
                          >
                            {index + 1}. {song.title}
                            {/* {isCurrentlyPlaying && (
                              <span
                                className="badge bg-success ms-2"
                                style={{ fontSize: "0.7em" }}
                              >
                                Playing
                              </span>
                            )} */}
                          </h4>
                          <span
                            onClick={() => sendToArtist(ArtistId)}
                            style={{
                              cursor: ArtistId ? "pointer" : "default",
                              color: "#1DB954",
                            }}
                          >
                            {song.artist || "Unknown Artist"}
                          </span>
                        </div>

                        <div className="dropdown">
                          <span
                            className={`d-flex text-white align-items-center h5 mb-0 ${
                              isDropdownOpen ? "show" : ""
                            }`}
                            onClick={() => toggleDropdown(contentId)}
                          >
                            <i className="fas fa-ellipsis-h"></i>
                          </span>
                          <div
                            className={`dropdown-menu dropdown-menu-end ${
                              isDropdownOpen ? "show" : ""
                            }`}
                            style={
                              isDropdownOpen
                                ? {
                                    position: "absolute",
                                    inset: "0px 0px auto auto",
                                    margin: "0px",
                                    transform: "translate(0px, 19px)",
                                  }
                                : {}
                            }
                          >
                            <a
                              className="dropdown-item"
                              href="#!"
                              onClick={(e) => openPlaylistModal(song, e)}
                            >
                              <span className="material-icons">
                                library_music
                              </span>{" "}
                              Add To Playlist
                            </a>

                            <a
                              className="dropdown-item"
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                addToQueue(contentId);
                                setOpenDropdown(null);
                              }}
                            >
                              <span className="material-icons">
                                playlist_add
                              </span>{" "}
                              Add To Queue
                            </a>

                            <a
                              className={`dropdown-item ${
                                !ArtistId ? "disabled" : ""
                              }`}
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                if (ArtistId) {
                                  sendToArtist(ArtistId);
                                  setOpenDropdown(null);
                                }
                              }}
                            >
                              <span className="material-icons">music_note</span>{" "}
                              Go To Artist
                            </a>

                            <a
                              className={`dropdown-item ${
                                !isAlbhum ? "disabled" : ""
                              }`}
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                if (isAlbhum) {
                                  sendToAlbum(AlbhumId, isAlbhum);
                                  setOpenDropdown(null);
                                }
                              }}
                            >
                              <span className="material-icons">album</span> Go
                              To Album
                            </a>
                          </div>
                        </div>

                        <a
                          href="#!"
                          className={`single-item__add ${
                            isLiked ? "liked" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleLikeSong(contentId, isLiked);
                          }}
                        >
                          <i
                            className={
                              isLiked
                                ? "fas fa-heart text-danger"
                                : "far fa-heart"
                            }
                          ></i>
                        </a>

                        <a
                          href="#!"
                          className="single-item__export"
                          onClick={(e) => openSharePopup(song, e)}
                        >
                          <i className="fas fa-share"></i>
                        </a>

                        {/* <span className="single-item__time">
                          {isCurrentlyPlaying
                            ? `${formatDuration(seek)} / ${formatDuration(
                                duration,
                              )}`
                            : formatDuration(song.duration)}
                        </span> */}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="col-12">
            <div className="release" style={{ justifyContent: "center" }}>
              <h5 style={{ color: "white" }}>No songs found!</h5>
            </div>
          </div>
        )}
      </div>

      {sharePopup.open && sharePopup.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="share-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "350px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "25px",
              }}
            >
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#aaa7a7",
                }}
              >
                Share
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "#ccc",
                  fontSize: "14px",
                }}
              >
                {sharePopup.song.title}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                marginBottom: "25px",
              }}
            >
              <button
                onClick={() => shareOnSocialMedia("facebook", sharePopup.song)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1877f2",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#1877f2",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <i
                    className="fab fa-facebook-f"
                    style={{ color: "white" }}
                  ></i>
                </div>
                Facebook
              </button>

              <button
                onClick={() => shareOnSocialMedia("twitter", sharePopup.song)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1da1f2",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#1da1f2",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <i className="fab fa-twitter" style={{ color: "white" }}></i>
                </div>
                Twitter
              </button>

              <button
                onClick={() => shareOnSocialMedia("whatsapp", sharePopup.song)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#25d366",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#25d366",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <i className="fab fa-whatsapp" style={{ color: "white" }}></i>
                </div>
                Whatsapp
              </button>

              <button
                onClick={() => shareOnSocialMedia("telegram", sharePopup.song)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#0088cc",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#0088cc",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <i className="fab fa-telegram" style={{ color: "white" }}></i>
                </div>
                Telegram
              </button>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <input
                type="text"
                value={generateShareableUrl(
                  sharePopup.song._id,
                  sharePopup.song.title,
                )}
                readOnly
                style={{
                  flex: 1,
                  padding: "12px 15px",
                  backgroundColor: "#2a2a2a",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  padding: "12px 20px",
                  backgroundColor: "#1DB954",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                COPY
              </button>
            </div>

            <button
              onClick={closeSharePopup}
              style={{
                width: "100%",
                marginTop: "20px",
                padding: "12px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #444",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* Playlist Modal */}
      {playlistModal.open && playlistModal.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="playlist-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "400px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#ffff",
                }}
              >
                Add to Playlist
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "#ccc",
                  fontSize: "14px",
                }}
              >
                {playlistModal.song.title}
              </p>
            </div>

            {!showCreateForm ? (
              <>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginBottom: "20px",
                  }}
                >
                  {playlists.length > 0 ? (
                    playlists.map((playlist) => (
                      <div
                        key={playlist._id}
                        style={{
                          padding: "12px",
                          border: "1px solid #333",
                          borderRadius: "8px",
                          marginBottom: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                        onClick={() =>
                          addToPlaylist(playlist._id, playlistModal.song._id)
                        }
                      >
                        <img
                          src={
                            playlist.playlistCoverUrl ||
                            "/img/live/songDefault.png"
                          }
                          alt={playlist.name}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            {playlist.name}
                          </div>
                          {playlist.description && (
                            <div style={{ fontSize: "12px", color: "#ccc" }}>
                              {playlist.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#ccc",
                      }}
                    >
                      No playlists found
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowCreateForm(true)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1DB954",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  Create New Playlist
                </button>
              </>
            ) : (
              <div>
                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Playlist Name *
                  </label>
                  <input
                    type="text"
                    value={newPlaylist.name}
                    onChange={(e) =>
                      setNewPlaylist((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter playlist name"
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    value={newPlaylist.description}
                    onChange={(e) =>
                      setNewPlaylist((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter playlist description"
                    rows="3"
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "14px",
                    }}
                  >
                    Cover Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{
                      width: "100%",
                      padding: "8px",
                      backgroundColor: "#2a2a2a",
                      border: "1px solid #444",
                      borderRadius: "6px",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={createNewPlaylist}
                    disabled={playlistLoading}
                    style={{
                      flex: 1,
                      padding: "12px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: playlistLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      opacity: playlistLoading ? 0.7 : 1,
                    }}
                  >
                    {playlistLoading ? "Creating..." : "Create Playlist"}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      flex: 1,
                      padding: "12px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #444",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showCreateForm && (
              <button
                onClick={closePlaylistModal}
                style={{
                  width: "100%",
                  marginTop: "10px",
                  padding: "12px",
                  backgroundColor: "transparent",
                  color: "#ccc",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Trending;
